import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { openLanding, PLUSZ_PATH } from '../lib/openLanding';
import { track } from '../lib/track';
import { resourceUrl, limitMessage, fileLabel, formatLine, LEVEL_LABEL, cardActions, pdfAllapot, pdfButtonLabel } from '../lib/moduleResources';
import { olvasoListaCached } from '../lib/olvaso';
import { kitoltesListaCached, kitoltesSor } from '../lib/olvasoKitoltes';
import { keszletSlughoz } from '../config/felismero';

/* ══════════════════════════════════════════════════════════════════════════
   SEGÉDLETEK EHHEZ A MODULHOZ (Plusz-segédletek 1. kör, B)
   ─────────────────────────────────────────────────────────────────────────
   A modulkártya alján (zárt modulnál is) és a lecke oldalán (variant="box").
   Nyitott elem: fájlonként letöltő gomb (sötét / nyomtatható PDF, Excel).
   Zárt elem: lakat, a szint neve, és a nyitó gomb (Plusz → /plusz#arak,
   TAG → TAG-aktiválás, PRO → /pro).
   Mérés: resource_cta_click {slug, hely, zart}; a zárt Plusz-elemnél
   plusz_cta_click {hely: "egyetem_segedlet"} is.
   Olvasó (1. kör): az appban olvasható anyagnál (olvaso_lista) a letöltőgombok
   fölött „Olvasás” / „Folytatás” fő gomb és vékony haladássáv; zárolt anyagnál
   „Tartalomjegyzék” link az olvasó előnézetére. A letöltés nem változik.
   Alakzatfelismerő (1. kör): az alakzatfelismero-gyakorlofuzet elemnél „Interaktív változat” link
   (/alakzatfelismero; a 12 ingyenes feladat zárt füzetnél is nyitva). Gyertyaminta-felismerő (1. kör):
   ugyanígy a gyertyaminta-puska és a gyertyaminta-atlasz elemnél (/gyertyafelismero); config/felismero.js.
   Olvasó 3. kör: a PDF-anyagnál (a `szabad` kivételével) a PDF-letöltés csak PRO-nak és az
   átmenetben lévő Plusz-előfizetőknek jár: egy másodlagos „PDF” gomb a hátralévő kerettel
   (segedlet_pdf_allapot); Plusz-fióknak egy halk „PDF-letöltés a PRO-ban” sor; másnak az
   „Olvasás” elég. Az Excel nem változik. Mérés: segedlet_pdf_elutasitva, segedlet_pdf_pro_cta_click.
   Olvasó 4. kör: a kitölthető anyagnál a haladás mellett egy rövid sor (olvaso_kitoltes_lista):
   „12 / 24 kipipálva”, „3 terv”, „utoljára: 44. hét”.
   ══════════════════════════════════════════════════════════════════════════ */

const CYAN = '#00D2C8';
const PURPLE = '#A97BFF';
const UNLOCK = {
  tag: { text: 'TAG-gal nyílik', path: '/dashboard?tag=aktivalas' },
  plusz: { text: 'Plusszal nyílik', path: PLUSZ_PATH },
  pro: { text: 'PRO-val nyílik', path: '/pro' },
};

function ReadBlock({ o, session, hely, kitoltes }) {
  if (!o) return null;
  const sor = kitoltesSor(kitoltes);
  const pct = o.lapszam ? Math.round(((o.latott_db || 0) / o.lapszam) * 100) : 0;
  if (!o.elerheto) {
    return (
      <Link to={`/olvaso/${o.slug}`} onClick={() => track(session, 'olvaso_cta_click', { slug: o.slug, szint: o.szint, hely: `${hely}_tartalom` })}
        style={{ display: 'inline-block', fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 6 }}>
        Tartalomjegyzék ({o.lapszam} lap) →
      </Link>
    );
  }
  return (
    <div style={{ marginTop: 8 }}>
      {o.latott_db > 0 && (
        <div aria-hidden="true" style={{ height: 3, background: 'rgba(255,255,255,0.09)', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
          <i style={{ display: 'block', height: '100%', width: `${pct}%`, background: CYAN }} />
        </div>
      )}
      <Link to={`/olvaso/${o.slug}`} onClick={() => track(session, 'olvaso_cta_click', { slug: o.slug, szint: o.szint, hely })}
        style={{ display: 'block', textAlign: 'center', background: CYAN, color: '#0a0f1e', borderRadius: 8, padding: '8px 10px',
          fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
        {o.latott_db > 0 ? `Folytatás · ${o.latott_db} / ${o.lapszam} lap` : `Olvasás · ${o.lapszam} lap`}
      </Link>
      {sor && <div data-testid="kitoltes-sor" style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 5, textAlign: 'center' }}>✎ {sor}</div>}
    </div>
  );
}

function Item({ r, session, hely, olvaso, kitoltes, pdf, onPdfChange }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);     // null | hibaüzenet
  const files = r.fajlok || [];

  const download = async (f) => {
    track(session, 'resource_cta_click', { slug: r.slug, hely, zart: false });
    setBusy(f.fajl); setError(null);
    const res = await resourceUrl(r.slug, f.fajl);
    setBusy(null);
    if (res?.appban) {
      track(session, 'segedlet_pdf_elutasitva', { slug: r.slug, szint: r.szint });
      setError('Ez az anyag az appban olvasható; a PDF-letöltés a PRO-csomag része.');
      onPdfChange?.();
      return;
    }
    if (res && 'korlat' in res) {
      track(session, 'resource_limit_hit', { slug: r.slug, korlat: res.korlat });
      setError(limitMessage(res.korlat));
      return;
    }
    if (!res?.url) { setError('A letöltés most nem sikerült. Próbáld újra pár másodperc múlva.'); return; }
    if (mode === 'pdf') onPdfChange?.();
    window.location.href = res.url;
  };
  const unlock = () => {
    track(session, 'resource_cta_click', { slug: r.slug, hely, zart: true });
    if (r.szint === 'plusz') track(session, 'plusz_cta_click', { hely: 'egyetem_segedlet' });
    openLanding((UNLOCK[r.szint] || UNLOCK.plusz).path);
  };

  const btn = {
    background: 'rgba(0,210,200,0.1)', border: '0.5px solid rgba(0,210,200,0.35)', color: CYAN,
    borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  };
  const lockBtn = { ...btn, background: r.szint === 'pro' ? PURPLE : CYAN, border: 'none', color: '#0a0f1e' };
  const mode = cardActions(r, pdf);
  const maradek = pdf?.anyagok?.[r.slug];
  const pdfOff = maradek === 0 || pdf?.napi_maradek === 0;
  const felismero = keszletSlughoz(r.slug);

  return (
    <li style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '8px 0', borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
      <div style={{ minWidth: 0, flex: '1 1 180px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: r.can_download ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.55)' }}>
          {!r.can_download && <span aria-label="Zárt segédlet">🔒</span>}
          <span>{r.cim}</span>
          <span style={{
            fontSize: 10, fontWeight: 600, borderRadius: 6, padding: '1px 6px', whiteSpace: 'nowrap',
            color: r.szint === 'plusz' ? CYAN : r.szint === 'pro' ? PURPLE : 'rgba(255,255,255,0.55)',
            border: `0.5px solid ${r.szint === 'plusz' ? 'rgba(0,210,200,0.4)' : r.szint === 'pro' ? 'rgba(169,123,255,0.4)' : 'rgba(255,255,255,0.15)'}`,
          }}>{LEVEL_LABEL[r.szint] || r.szint}</span>
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{formatLine(r)}</div>
        {error && <div role="alert" style={{ fontSize: 11, color: '#fca5a5', marginTop: 4 }}>{error}</div>}
        <ReadBlock o={olvaso} session={session} hely={hely} kitoltes={kitoltes} />
        {felismero && (
          <Link to={felismero.path} onClick={() => track(session, `${felismero.ev}_cta_click`, { szint: r.szint, hely: `${hely}_interaktiv` })}
            style={{ display: 'inline-block', fontSize: 12, color: CYAN, fontWeight: 600, marginTop: 6 }}>
            {felismero.ikon} Interaktív változat →
          </Link>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {mode === 'fajlok' && files.map(f => (
          <button key={f.fajl} type="button" style={btn} disabled={busy === f.fajl} onClick={() => download(f)}>
            {busy === f.fajl ? 'Letöltés…' : `↓ ${fileLabel(f)}`}
          </button>
        ))}
        {mode === 'pdf' && (
          <>
            <button type="button" style={{ ...btn, opacity: pdfOff ? 0.5 : 1 }} disabled={pdfOff || Boolean(busy)} onClick={() => download(files[0])}>
              {busy ? 'Letöltés…' : `↓ ${pdfButtonLabel(maradek, pdf?.napi_maradek)}`}
            </button>
            {files[1] && !pdfOff && (
              <button type="button" style={{ ...btn, background: 'none', border: 'none', padding: '6px 4px' }} disabled={Boolean(busy)} onClick={() => download(files[1])}>
                Nyomtatható
              </button>
            )}
          </>
        )}
        {mode === 'pro_sor' && (
          <button type="button" style={{ background: 'none', border: 'none', padding: '6px 0', color: 'rgba(255,255,255,0.5)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}
            onClick={() => { track(session, 'segedlet_pdf_pro_cta_click', { slug: r.slug, hely }); openLanding('/pro#arak'); }}>
            PDF-letöltés a PRO-ban
          </button>
        )}
        {mode === 'unlock' && <button type="button" style={lockBtn} onClick={unlock}>{(UNLOCK[r.szint] || UNLOCK.plusz).text}</button>}
      </div>
    </li>
  );
}

export default function ModuleResources({ items, session, variant = 'card', hely = 'egyetem_modul' }) {
  const [olvaso, setOlvaso] = useState({});
  const [pdf, setPdf] = useState(null);
  const [kitoltes, setKitoltes] = useState({});
  useEffect(() => {
    let alive = true;
    if (items?.length) olvasoListaCached().then(l => alive && setOlvaso(Object.fromEntries(l.map(o => [o.slug, o]))));
    if (items?.length && session) kitoltesListaCached().then(k => alive && setKitoltes(k));
    if (items?.length && session) pdfAllapot().then(p => alive && setPdf(p));
    return () => { alive = false; };
  }, [items, session]);
  const onPdfChange = () => pdfAllapot({ refresh: true }).then(setPdf);
  if (!items?.length) return null;
  const box = variant === 'box';
  return (
    <section aria-label="Segédletek ehhez a modulhoz" style={box
      ? { background: '#1a1d2e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '14px 16px', marginBottom: 24 }
      : { marginTop: 10, padding: '10px 12px 4px', background: 'rgba(255,255,255,0.02)', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
        📎 Segédletek ehhez a modulhoz
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {items.map(r => <Item key={r.slug} r={r} session={session} hely={hely} olvaso={olvaso[r.slug]} kitoltes={kitoltes[r.slug]} pdf={pdf} onPdfChange={onPdfChange} />)}
      </ul>
    </section>
  );
}
